import React, { useState, useMemo } from 'react';
import { CaretLeft, CaretRight, Warning } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { format, addMonths, subMonths } from 'date-fns';
import { es } from 'date-fns/locale';
import { CalendarDayCell } from './CalendarDayCell';
import { CalendarDayDetailDialog } from './CalendarDayDetailDialog';
import { CalendarMovDetailDialog } from './CalendarMovDetailDialog';
import { useCalendarData } from '@/hooks/useCalendarData';

const DAYS_OF_WEEK = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

const LEGEND = [
  { label: 'Agendado', color: '#6C757D', bgColor: '#6C757D20' },
  { label: 'En Espera de Asignación', color: '#0891B2', bgColor: '#0891B220' },
  { label: 'Asignado', color: '#008630', bgColor: '#00863020' },
];

const CalendarHeader = ({ currentMonth, setCurrentMonth, totalConflictsInMonth }) => (
  <div className="flex items-center justify-between mb-6">
    <div className="flex items-center gap-4">
      <Button
        data-testid="calendar-prev-month"
        variant="outline"
        size="sm"
        onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
        className="border-[#E9ECEF] hover:bg-[#F8F9FA] transition-colors"
      >
        <CaretLeft size={18} weight="bold" />
      </Button>
      <h2
        data-testid="calendar-month-title"
        className="text-2xl font-black font-['Chivo'] tracking-tight text-[#0A0A0A] capitalize min-w-[220px]"
      >
        {format(currentMonth, 'MMMM yyyy', { locale: es })}
      </h2>
      <Button
        data-testid="calendar-next-month"
        variant="outline"
        size="sm"
        onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
        className="border-[#E9ECEF] hover:bg-[#F8F9FA] transition-colors"
      >
        <CaretRight size={18} weight="bold" />
      </Button>
      <Button
        data-testid="calendar-today"
        variant="outline"
        size="sm"
        onClick={() => setCurrentMonth(new Date())}
        className="border-[#E9ECEF] hover:bg-[#F8F9FA] font-['IBM_Plex_Sans'] transition-colors"
      >
        Hoy
      </Button>
    </div>

    {totalConflictsInMonth > 0 && (
      <div className="flex items-center gap-2 px-4 py-2 bg-[#FF3B30]/10 border border-[#FF3B30]">
        <Warning size={18} weight="bold" className="text-[#FF3B30]" />
        <span className="text-sm font-medium font-['IBM_Plex_Sans'] text-[#0A0A0A]">
          {totalConflictsInMonth} {totalConflictsInMonth === 1 ? 'día con conflicto' : 'días con conflictos'} de horario
        </span>
      </div>
    )}
  </div>
);

const CalendarLegend = () => (
  <div className="flex items-center gap-4 mt-4 flex-wrap">
    {LEGEND.map((item) => (
      <div key={item.label} className="flex items-center gap-2">
        <div className="w-3 h-3" style={{ backgroundColor: item.bgColor, border: `1px solid ${item.color}` }}></div>
        <span className="text-xs text-[#495057] font-['IBM_Plex_Sans']">{item.label}</span>
      </div>
    ))}
    <div className="flex items-center gap-2">
      <Warning size={12} weight="bold" className="text-[#FF3B30]" />
      <span className="text-xs text-[#495057] font-['IBM_Plex_Sans']">Conflicto de horario</span>
    </div>
  </div>
);

export const CalendarView = ({ movilizaciones }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedMov, setSelectedMov] = useState(null);

  const { days, movsByDay, conflictsByDay, totalConflictsInMonth } = useCalendarData(movilizaciones, currentMonth);

  const selectedDayMovs = useMemo(() => {
    if (!selectedDay) return [];
    return movsByDay.get(format(selectedDay, 'yyyy-MM-dd')) || [];
  }, [selectedDay, movsByDay]);

  return (
    <div className="bg-white border border-[#E9ECEF] p-6">
      <CalendarHeader
        currentMonth={currentMonth}
        setCurrentMonth={setCurrentMonth}
        totalConflictsInMonth={totalConflictsInMonth}
      />

      <div className="grid grid-cols-7 mb-2">
        {DAYS_OF_WEEK.map((dow) => (
          <div
            key={dow}
            className="p-2 text-xs font-bold uppercase tracking-wide text-[#6C757D] font-['IBM_Plex_Sans'] text-center"
          >
            {dow}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-px bg-[#E9ECEF] border border-[#E9ECEF]">
        {days.map((day) => {
          const key = format(day, 'yyyy-MM-dd');
          return (
            <CalendarDayCell
              key={key}
              day={day}
              currentMonth={currentMonth}
              dayMovs={movsByDay.get(key) || []}
              hasConflict={conflictsByDay.get(key)}
              onSelect={setSelectedDay}
            />
          );
        })}
      </div>

      <CalendarLegend />

      <CalendarDayDetailDialog
        selectedDay={selectedDay}
        selectedDayMovs={selectedDayMovs}
        onClose={() => setSelectedDay(null)}
        onSelectMov={setSelectedMov}
      />

      <CalendarMovDetailDialog
        selectedMov={selectedMov}
        onClose={() => setSelectedMov(null)}
      />
    </div>
  );
};
